import { useCallback, useEffect, useState } from 'react';
import { Download, Printer, Landmark } from '../../lib/icons';
import { fetchRegistreCalamites } from '../../lib/dataProvider';
import { fmt } from '../shared/badges';
import { PrintDoc } from '../print/PrintDoc';

const th = 'px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';
const inputCls =
  'h-10 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none focus:border-slate-900 focus:bg-white';

const debutCampagne = () => `${new Date().getFullYear()}-01-01`;

export const RegistreCalamites = () => {
  const [du, setDu] = useState(debutCampagne);
  const [au, setAu] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [etat, setEtat] = useState(false);

  const load = useCallback(() => {
    fetchRegistreCalamites({ du, au }).then(setData);
  }, [du, au]);
  useEffect(() => {
    load();
  }, [load]);

  if (!data) return <p className="p-6 text-sm text-slate-500">Chargement du registre…</p>;
  const t = data.totaux;

  const exportCsv = () => {
    const head = ['N° document', 'Réf', 'Date', 'Fournisseur', 'MF', 'Qualité', 'Poids net (kg)', 'Montant brut (TND)', 'Retenue 1% (TND)', 'Dette fonds (TND)', 'Charge huilerie (TND)', 'Net à payer (TND)'];
    const corps = data.lignes.map((l) => [l.numero || l.ref, l.ref, l.date, l.tiers, l.mf, l.qualite, l.poidsNet, l.montantBrut, l.retenue, l.provision, l.charge, l.net]);
    const csv = [head, ...corps, ['', '', '', 'TOTAL', '', '', t.poidsNet, t.montantBrut, t.retenue, t.provision, t.charge, t.net]]
      .map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';'))
      .join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' }));
    a.download = `registre-calamites-${du}_${au}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <section data-testid="registre-calamites" className="space-y-5">
      <div className="border border-slate-200 bg-white">
        <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Du</label>
              <input data-testid="calamites-du" type="date" value={du} onChange={(e) => setDu(e.target.value)} className={`${inputCls} mt-1`} />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Au</label>
              <input data-testid="calamites-au" type="date" value={au} onChange={(e) => setAu(e.target.value)} className={`${inputCls} mt-1`} />
            </div>
            <button
              type="button"
              data-testid="calamites-toute-campagne"
              onClick={() => {
                setDu(debutCampagne());
                setAu(new Date().toISOString().slice(0, 10));
              }}
              className="h-10 border border-slate-300 px-3 text-sm font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-100"
            >
              Toute la campagne
            </button>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="calamites-export"
              onClick={exportCsv}
              className="flex h-10 items-center gap-2 border border-slate-300 px-3 text-sm font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
            >
              <Download size={15} /> Export CSV
            </button>
            <button
              type="button"
              data-testid="calamites-etat"
              onClick={() => setEtat(true)}
              className="flex h-10 items-center gap-2 bg-slate-900 px-3 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700"
            >
              <Printer size={15} /> État déclaratif
            </button>
          </div>
        </div>

        <dl className="grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-4">
          {[
            ['Bons concernés', fmt(t.bons), 'calamites-kpi-bons'],
            ['Base brute (TND)', fmt(t.montantBrut, 3), 'calamites-kpi-brut'],
            ['Dette fonds 1 % (TND)', fmt(t.provision, 3), 'calamites-kpi-retenue'],
            ['Charge huilerie (TND)', fmt(t.charge, 3), 'calamites-kpi-charge'],
            ['Net à payer fournisseurs (TND)', fmt(t.net, 3), 'calamites-kpi-net'],
          ].map(([k, v, id]) => (
            <div key={k} className="bg-white px-4 py-3">
              <dt className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{k}</dt>
              <dd data-testid={id} className="tabular mt-1 font-data text-xl font-medium text-slate-900">{v}</dd>
            </div>
          ))}
        </dl>
        <p className="border-t border-slate-200 bg-slate-50 px-4 py-2 font-data text-xs text-slate-600">
          <Landmark size={12} className="mr-1 inline" />
          Fonds de {data.taux} % dû sur les achats d'olives valorisés définitivement.
          Case décochée : charge de l’huilerie, sans réduction du net fournisseur.
          Bons annulés, pesées en cours et valorisations provisoires exclus. Aucun mouvement de caisse.
        </p>
      </div>

      <div className="overflow-x-auto border border-slate-200 bg-white">
        <table className="w-full border-collapse text-left" data-testid="table-calamites">
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              {['N° document', 'Réf.', 'Date', 'Fournisseur', 'MF', 'Qualité', 'Poids net (Kg)', 'Base brute (TND)', 'Retenue 1 % (TND)', 'Dette fonds (TND)', 'Charge huilerie (TND)', 'Net à payer (TND)'].map((c) => (
                <th key={c} className={th}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.lignes.map((l) => (
              <tr key={l.ref} data-testid={`calamites-ligne-${l.ref}`} className="hover:bg-slate-50">
                <td className="px-3 py-2 font-data text-sm text-slate-500">{l.numero || 'non émis'}</td>
                <td className="px-3 py-2 font-data text-sm font-semibold text-slate-900">{l.ref}</td>
                <td className="px-3 py-2 font-data text-sm text-slate-700">{l.date}</td>
                <td className="px-3 py-2 text-sm text-slate-900">{l.tiers}</td>
                <td className="px-3 py-2 font-data text-xs text-slate-500">{l.mf || '—'}</td>
                <td className="px-3 py-2 text-sm text-slate-700">{l.qualite}</td>
                <td className="tabular px-3 py-2 font-data text-sm text-slate-700">{fmt(l.poidsNet)}</td>
                <td className="tabular px-3 py-2 font-data text-sm text-slate-700">{fmt(l.montantBrut, 3)}</td>
                <td className="tabular px-3 py-2 font-data text-sm font-semibold text-amber-700">{fmt(l.retenue, 3)}</td>
                <td className="tabular px-3 py-2 font-data text-sm">{fmt(l.provision, 3)}</td>
                <td className="tabular px-3 py-2 font-data text-sm">{fmt(l.charge, 3)}</td>
                <td className="tabular px-3 py-2 font-data text-sm text-slate-900">{fmt(l.net, 3)}</td>
              </tr>
            ))}
            {data.lignes.length === 0 && (
              <tr>
                <td colSpan={12} data-testid="calamites-vide" className="px-3 py-6 text-center text-sm text-slate-500">
                  Aucun achat définitivement valorisé sur cette période.
                </td>
              </tr>
            )}
          </tbody>
          {data.lignes.length > 0 && (
            <tfoot className="border-t-2 border-slate-900 bg-slate-50">
              <tr>
                <td colSpan={6} className="px-3 py-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-600">
                  Total période — {du} au {au}
                </td>
                <td className="tabular px-3 py-2 font-data text-sm font-semibold">{fmt(t.poidsNet)}</td>
                <td className="tabular px-3 py-2 font-data text-sm font-semibold">{fmt(t.montantBrut, 3)}</td>
                <td data-testid="calamites-total-retenue" className="tabular px-3 py-2 font-data text-sm font-bold text-amber-700">{fmt(t.retenue, 3)}</td>
                <td className="tabular px-3 py-2 font-data text-sm font-semibold">{fmt(t.provision, 3)}</td>
                <td className="tabular px-3 py-2 font-data text-sm font-semibold">{fmt(t.charge, 3)}</td>
                <td className="tabular px-3 py-2 font-data text-sm font-semibold">{fmt(t.net, 3)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {data.parFournisseur.length > 0 && (
        <div className="overflow-x-auto border border-slate-200 bg-white">
          <p className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
            Récapitulatif par fournisseur (pour la déclaration)
          </p>
          <table className="w-full border-collapse text-left" data-testid="table-calamites-fournisseurs">
            <thead className="border-b border-slate-200">
              <tr>
                {['Fournisseur', 'Matricule fiscal', 'Bons', 'Base brute (TND)', 'Retenue 1 % (TND)', 'Dette fonds (TND)', 'Charge huilerie (TND)'].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.parFournisseur.map((f) => (
                <tr key={f.tiers} className="hover:bg-slate-50">
                  <td className="px-3 py-2 text-sm font-semibold text-slate-900">{f.tiers}</td>
                  <td className="px-3 py-2 font-data text-xs text-slate-500">{f.mf || '—'}</td>
                  <td className="tabular px-3 py-2 font-data text-sm text-slate-700">{f.bons}</td>
                  <td className="tabular px-3 py-2 font-data text-sm text-slate-700">{fmt(f.montantBrut, 3)}</td>
                  <td className="tabular px-3 py-2 font-data text-sm font-semibold text-amber-700">{fmt(f.retenue, 3)}</td>
                  <td className="px-3 py-2 font-data text-sm">{fmt(f.provision, 3)}</td>
                  <td className="px-3 py-2 font-data text-sm">{fmt(f.charge, 3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {etat && (
        <PrintDoc
          type="État déclaratif — Fonds des Calamités (1 %)"
          numero={`FC-${du}_${au}`}
          date={new Date().toISOString().slice(0, 10)}
          format="a4"
          lignes={[
            ['Période déclarée', `du ${du} au ${au}`],
            ['Nature', 'Provision de 1 % sur les achats d’olives valorisés définitivement'],
            ['Nombre de bons concernés', fmt(t.bons)],
            ['Poids net d’olives acheté', `${fmt(t.poidsNet)} Kg`],
            ['Fournisseurs concernés', fmt(data.parFournisseur.length)],
          ]}
          totaux={[
            ['Base brute des achats', `${fmt(t.montantBrut, 3)} TND`],
            ['Net à payer aux fournisseurs', `${fmt(t.net, 3)} TND`],
            ['Retenue sur les fournisseurs', `${fmt(t.retenue, 3)} TND`],
            ['Charge supportée par l’huilerie', `${fmt(t.charge, 3)} TND`],
            ['Fonds de calamité dû', `${fmt(t.provision, 3)} TND`],
          ]}
          mentions="Montants en dinars tunisiens. La dette du fonds inclut les retenues et les montants pris en charge par l’huilerie. Les paiements effectifs à l’État restent à rapprocher ; cet état ne représente pas un solde après reversements."
          signatures={['Signature & cachet — Huilerie', 'Réception — Administration']}
          onClose={() => setEtat(false)}
        />
      )}
    </section>
  );
};
